/* eslint-disable no-param-reassign */

import { createAsyncThunk, createEntityAdapter, createSlice, EntityState, SerializedError } from '@reduxjs/toolkit'
import { AppThunk } from '.'
import {
  TownListResponse,
  getTownList,
  WorkplaceDistrictListResponse,
  getWorkplaceDistrictList,
  getWorkplaceTypeList,
  WorkplaceTypeListResponse,
  WorkplaceListResponse,
  getWorkplaceList,
  WorkplaceListRequest,
  PeriodListResponse,
  getPeriodList,
} from '../api/registration'
import { RootState } from './rootReducer'

interface State {
  towns: EntityState<TownListResponse>
  townsLoading: 'idle' | 'pending'
  townsError: null | SerializedError

  workplaceDistricts: WorkplaceDistrictListResponse[] | null
  workplaceDistrictsLoading: 'idle' | 'pending'
  workplaceDistrictsError: null | SerializedError

  workplaceTypes: WorkplaceTypeListResponse[] | null
  workplaceTypesLoading: 'idle' | 'pending'
  workplaceTypesError: null | SerializedError

  workplaces: WorkplaceListResponse[] | null
  workplacesLoading: 'idle' | 'pending'
  workplacesError: null | SerializedError

  workplacesFiltered: WorkplaceListResponse[] | null
  workplacesFilteredLoading: 'idle' | 'pending'

  periods: EntityState<PeriodListResponse>
  periodsLoading: 'idle' | 'pending'
  periodsError: null | SerializedError
}

export const fetchTowns = createAsyncThunk<
  TownListResponse[],
  void,
  {
    state: RootState
  }
>('createApplication/fetchTowns', async () => {
  const response = await getTownList()
  return response.data
})

export const fetchWorkplaceDistricts = createAsyncThunk<
  WorkplaceDistrictListResponse[],
  string,
  {
    state: RootState
  }
>('createApplication/fetchWorkplaceDistricts', async (townId) => {
  const response = await getWorkplaceDistrictList(townId)
  return response.data
})

export const fetchWorkplaceTypes = createAsyncThunk<
  WorkplaceTypeListResponse[],
  string,
  {
    state: RootState
  }
>('createApplication/fetchWorkplaceTypes', async (townId) => {
  const response = await getWorkplaceTypeList(townId)
  return response.data
})

export const fetchWorkplaces = createAsyncThunk<
  WorkplaceListResponse[],
  WorkplaceListRequest,
  {
    state: RootState
  }
>('createApplication/fetchWorkplaces', async (request) => {
  const response = await getWorkplaceList(request)
  return response.data
})

export const fetchWorkplacesFiltered = createAsyncThunk<
  WorkplaceListResponse[],
  WorkplaceListRequest,
  {
    state: RootState
  }
>('createApplication/fetchWorkplacesFiltered', async (request) => {
  const response = await getWorkplaceList(request)
  return response.data
})

export const fetchPeriods = createAsyncThunk<
  PeriodListResponse[],
  { town: string; ssn: string },
  {
    state: RootState
  }
>('createApplication/fetchPeriods', async ({ town, ssn }) => {
  const response = await getPeriodList(town, ssn)
  return response.data
})

export const periodsAdapter = createEntityAdapter<PeriodListResponse>()
export const townsAdapter = createEntityAdapter<TownListResponse>()

const initialState: State = {
  towns: townsAdapter.getInitialState(),
  townsLoading: 'idle',
  townsError: null,

  workplaceDistricts: null,
  workplaceDistrictsLoading: 'idle',
  workplaceDistrictsError: null,

  workplaceTypes: null,
  workplaceTypesLoading: 'idle',
  workplaceTypesError: null,

  workplaces: null,
  workplacesLoading: 'idle',
  workplacesError: null,

  workplacesFiltered: null,
  workplacesFilteredLoading: 'idle',

  periods: periodsAdapter.getInitialState(),
  periodsLoading: 'idle',
  periodsError: null,
}

const slice = createSlice({
  name: 'createApplication',
  initialState,
  reducers: {
    resetWorkplacesFiltered(state) {
      state.workplacesFiltered = state.workplaces
    },
  },
  extraReducers: (builder) => {
    /*
     * fetchTowns
     */
    builder.addCase(fetchTowns.pending, (state) => {
      state.townsError = null
      state.townsLoading = 'pending'
      state.workplaceDistricts = null
      state.workplaceTypes = null
      state.workplaces = null

      townsAdapter.setAll(state.towns, [])
      periodsAdapter.setAll(state.periods, [])
    })
    builder.addCase(fetchTowns.fulfilled, (state, { payload }) => {
      state.townsLoading = 'idle'

      townsAdapter.setAll(state.towns, payload)
    })
    builder.addCase(fetchTowns.rejected, (state, action) => {
      state.townsError = action.error
      state.townsLoading = 'idle'
    })
    /*
     * fetchWorkplaceDistricts
     */
    builder.addCase(fetchWorkplaceDistricts.pending, (state) => {
      state.workplaceDistricts = null
      state.workplaceDistrictsError = null
      state.workplaceDistrictsLoading = 'pending'
    })
    builder.addCase(fetchWorkplaceDistricts.fulfilled, (state, { payload }) => {
      state.workplaceDistricts = payload
      state.workplaceDistrictsLoading = 'idle'
    })
    builder.addCase(fetchWorkplaceDistricts.rejected, (state, action) => {
      state.workplaceDistrictsError = action.error
      state.workplaceDistrictsLoading = 'idle'
    })
    /*
     * fetchWorkplaceTypes
     */
    builder.addCase(fetchWorkplaceTypes.pending, (state) => {
      state.workplaceTypes = null
      state.workplaceTypesError = null
      state.workplaceTypesLoading = 'pending'
    })
    builder.addCase(fetchWorkplaceTypes.fulfilled, (state, { payload }) => {
      state.workplaceTypes = payload
      state.workplaceTypesLoading = 'idle'
    })
    builder.addCase(fetchWorkplaceTypes.rejected, (state, action) => {
      state.workplaceTypesError = action.error
      state.workplaceTypesLoading = 'idle'
    })
    /*
     * fetchWorkplaces
     */
    builder.addCase(fetchWorkplaces.pending, (state) => {
      state.workplaces = null
      state.workplacesError = null
      state.workplacesLoading = 'pending'
      state.workplacesFiltered = null
      state.workplacesFilteredLoading = 'pending'
    })
    builder.addCase(fetchWorkplaces.fulfilled, (state, { payload }) => {
      state.workplaces = payload
      state.workplacesLoading = 'idle'
      state.workplacesFiltered = payload
      state.workplacesFilteredLoading = 'idle'
    })
    builder.addCase(fetchWorkplaces.rejected, (state, action) => {
      state.workplacesError = action.error
      state.workplacesLoading = 'idle'
      state.workplacesFilteredLoading = 'idle'
    })
    /*
     * fetchWorkplacesFiltered
     */
    builder.addCase(fetchWorkplacesFiltered.pending, (state) => {
      state.workplacesFiltered = null
      state.workplacesError = null
      state.workplacesFilteredLoading = 'pending'
    })
    builder.addCase(fetchWorkplacesFiltered.fulfilled, (state, { payload }) => {
      state.workplacesFiltered = payload
      state.workplacesFilteredLoading = 'idle'
    })
    builder.addCase(fetchWorkplacesFiltered.rejected, (state, action) => {
      state.workplacesError = action.error
      state.workplacesFilteredLoading = 'idle'
    })
    /*
     * fetchPeriods
     */
    builder.addCase(fetchPeriods.pending, (state) => {
      state.periodsError = null
      state.periodsLoading = 'pending'

      periodsAdapter.setAll(state.periods, [])
    })
    builder.addCase(fetchPeriods.fulfilled, (state, { payload }) => {
      state.periodsLoading = 'idle'

      periodsAdapter.setAll(state.periods, payload)
    })
    builder.addCase(fetchPeriods.rejected, (state, action) => {
      state.periodsError = action.error
      state.periodsLoading = 'idle'
    })
  },
})

export const { resetWorkplacesFiltered } = slice.actions

export const periodsSelector = periodsAdapter.getSelectors((state: RootState) => state.createApplication.periods)
export const townsSelector = townsAdapter.getSelectors((state: RootState) => state.createApplication.towns)

export default slice.reducer

export const fetchTownDependencies =
  (town: string, ssn: string): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(fetchWorkplaceDistricts(town))
    dispatch(fetchWorkplaceTypes(town))
    dispatch(fetchWorkplaces({ town: town }))
    dispatch(fetchPeriods({ town, ssn }))
  }
